import get from 'lodash/get'
import trimEnd from 'lodash/trimEnd'
import { default as _join } from 'lodash/join'

function joinAndNormalize(args, sep) {
  return trimEnd(_join(args, sep).replace(/[/]+/g, '/'), '/')
}

function extractMajorVersionFromRoute(routeName) {
  const match = routeName.match(/^(five|six|seven|latest)\./)
  if (!match) return null
  const versionMap = { five: 5, six: 6, seven: 7, latest: 7 }
  return versionMap[match[1]]
}

function resolve({ to, current, append, routers }) {
  for (const router of routers) {
    if (!router) continue

    const res = router.resolve(to, current, append)

    if (res.resolved.matched.length > 0) {
      return { resolution: res, router }
    }
  }

  return { resolution: {}, router: null }
}

function guardEvent(e) {
  // don't redirect with control keys
  if (e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) {
    return
  }
  // don't redirect when preventDefault called
  if (e.defaultPrevented) {
    return
  }
  // don't redirect on right click
  if (e.button !== undefined && e.button !== 0) {
    return
  }
  // don't redirect if `target="_blank"`
  if (e.currentTarget && e.currentTarget.getAttribute) {
    var target = e.currentTarget.getAttribute('target')
    if (/\b_blank\b/i.test(target)) {
      return
    }
  }
  // this may be a Weex event which doesn't have this method
  if (e.preventDefault) {
    e.preventDefault()
  }
  return true
}

export default function (vue, params) {
  const { defaultLayout, fallbackRouter } = params || {}

  const RouterLink = vue.options.components.RouterLink

  if (!RouterLink) {
    throw new Error("Couldn't find the RouterLink from vue-router!")
  }

  if (!defaultLayout) {
    throw new Error('A defaultLayout is required when registering the SiteLink component')
  }

  return {
    name: 'SiteLink',

    extends: RouterLink,

    render(h) {
      const current = this.$route
      const isExternal = typeof this.to === 'string' && this.to.startsWith('http')

      const { resolution, router: matchingRouter } = resolve({
        to: this.to,
        current,
        append: this.append,
        routers: [this.$router, fallbackRouter]
      })

      const currentVersion = extractMajorVersionFromRoute(get(current, 'name', ''))
      const targetVersion = extractMajorVersionFromRoute(get(this.to, 'name', ''))
      const movingAcrossMajorVersion = currentVersion !== null && targetVersion !== null && currentVersion !== targetVersion

      const usedFallback = matchingRouter === fallbackRouter

      const layoutsDiffer = get(current, ['meta', 'layout'], defaultLayout) !== get(resolution.route, ['meta', 'layout'], defaultLayout)
      const resolvedName = get(resolution.route, ['name'], '')

      let args
      const p = (...pArgs) => {
        args = pArgs
      }

      RouterLink.extendOptions.render.call(this, p)

      if (movingAcrossMajorVersion || layoutsDiffer || usedFallback || isExternal || resolvedName === 'not.found') {
        // We remove the click handler to allow this navigation action
        // to bypass the vue-router and load from the browser.
        //
        // If the route name is resolved as 'not.found' that indicates that it
        // hit the catch-all route we have (or fallback route) in the Vue Router.
        // This indicates that the path is not something that is handled by our
        // Vue application and needs a full page load.
        if (args && args[1] && args[1].on && args[1].on.click) {
          delete args[1].on.click
        }
      }

      const { activeClass, exactActiveClass } = this

      const currentPath = joinAndNormalize([this.$router.history.base, current.path], '/')
      const toPath = joinAndNormalize([resolution.href])

      const isActive = currentPath.indexOf(toPath) > -1
      const isExactActive = currentPath === toPath

      if (this.custom && this.$scopedSlots.default) {
        const shouldReplace = this.replace

        var handler = function (e) {
          if (guardEvent(e)) {
            if (layoutsDiffer || usedFallback) {
              window.location.assign(resolution.href)
            } else {
              if (shouldReplace) {
                matchingRouter.replace(resolution.location, () => {})
              } else {
                matchingRouter.push(resolution.location, () => {})
              }
            }
          }
        }

        const scopedSlot = this.$scopedSlots.default({
          href: resolution.href,
          route: resolution.route,
          navigate: handler,
          isActive,
          isExactActive
        })

        if (!scopedSlot || !scopedSlot.length === 1) {
          throw new Error('A scoped slot must have exactly one child in it')
        }

        return scopedSlot[0]
      }

      if (usedFallback && args) {
        // Replace the href with the correct, resolved version from the fallback router
        const [, aCon] = args

        aCon.attrs.href = resolution.href
        aCon.class[activeClass] = isActive
        aCon.class[exactActiveClass] = isExactActive
      }

      if (isExternal && args) {
        const [, aCon] = args

        aCon.attrs.href = this.to
        aCon.attrs.rel = 'noopener'
      }

      return h(...args)
    }
  }
}
